import {
    Column,
    Container,
    Grid,
    Header,
    Label,
    PolicyAttention,
    PolicyBind,
    PolicyIssue,
    PolicyQuery,
    PolicyQuote,
    PolicyQuoteAccept,
    Row,
    Separator,
    useAlignments,
    useSizes,
} from 'metrix-common3';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import Badge from '../Badge';

const HeaderContentFlex = styled.div`
    display: flex;
    flex: 1;
    gap: 1rem;
    justify-content: ${({ $align }) => ($align === 'left' ? 'flex-start' : $align === 'right' ? 'flex-end' : 'center')};
    align-items: center;
    padding: 0 5px;
    margin-top: 30px;
    box-sizing: border-box;
    overflow: hidden;
`;

const ProfileSummaryHeader = (props) => {
    const intl = useIntl();
    const sizes = useSizes();
    const alignments = useAlignments();

    const renderIcon = (policyStatus) => {
        let icon = null;
        switch (policyStatus) {
            case 'incomplete':
            case 'submitted':
            case 'accepted':
                icon = <PolicyQuery />;
                break;
            case 'quoted':
            case 'quoteIssued':
                icon = <PolicyQuote />;
                break;
            case 'quoteAccepted':
                icon = <PolicyQuoteAccept />;
                break;
            case 'rejected':
            case 'abandoned':
            case 'lost':
                icon = <PolicyAttention />;
                break;
            case 'bindRequested':
            case 'bound':
                icon = <PolicyBind />;
                break;
            case 'issued':
                icon = <PolicyIssue />;
                break;
            default:
                icon = <PolicyBind />;
                break;
        }

        return icon;
    };

    const getBadgeColor = (status, type) => {
        const colors = {
            badgeAuthorizationStatus: {
                Authorized: 'green',
                'Not Authorized': 'red',
            },
            badgeDocumentIssueStatus: {
                'Policy Issued': 'green',
                'Proposal Issued': 'yellow',
                'Not Issued': 'red',
            },
            badgePaymentStatus: {
                Paid: 'green',
                'Not Paid': 'red',
            },
            badgePolicyStatusDescription: {
                None: 'grey',
                Incomplete: 'yellow',
                Submitted: 'yellow',
                Abandoned: 'yellow',
                Accepted: 'yellow',
                Quoted: 'yellow',
                'Quoted (indicative)': 'orange',
                'Underwriting Required': 'yellow',
                Referred: 'yellow',
                'Quote Issued': 'yellow',
                'Bind Requested': 'yellow',
                Bound: 'yellow',
                Issued: 'green',
                Lost: 'red',
                Cancelled: 'red',
                'Quote Accepted': 'yellow',
                Quoting: 'yellow',
            },
        };
        return colors[type][status] || 'grey';
    };

    const getBadgeText = (text, type) => {
        // Create a mapping of original text to translation IDs
        const mappings = {
            badgeAuthorizationStatus: {
                Authorized: 'Profile_Summary_Header.Badge.Authorized',
                'Not Authorized': 'Profile_Summary_Header.Badge.NotAuthorized',
            },
            badgeDocumentIssueStatus: {
                'Policy Issued': 'Profile_Summary_Header.Badge.PolicyIssued',
                'Proposal Issued': 'Profile_Summary_Header.Badge.ProposalIssued',
                'Not Issued': 'Profile_Summary_Header.Badge.NotIssued',
            },
            badgePaymentStatus: {
                Paid: 'Profile_Summary_Header.Badge.Paid',
                'Not Paid': 'Profile_Summary_Header.Badge.NotPaid',
            },
            badgePolicyStatusDescription: {
                None: 'Profile_Summary_Header.Badge.None',
                Incomplete: 'Profile_Summary_Header.Badge.Incomplete',
                Submitted: 'Profile_Summary_Header.Badge.Submitted',
                Abandoned: 'Profile_Summary_Header.Badge.Abandoned',
                Accepted: 'Profile_Summary_Header.Badge.Accepted',
                Quoted: 'Profile_Summary_Header.Badge.Quoted',
                'Quoted (indicative)': 'Profile_Summary_Header.Badge.QuotedIndicative',
                'Underwriting Required': 'Profile_Summary_Header.Badge.UnderwritingRequired',
                Referred: 'Profile_Summary_Header.Badge.Referred',
                'Quote Issued': 'Profile_Summary_Header.Badge.QuoteIssued',
                'Bind Requested': 'Profile_Summary_Header.Badge.BindRequested',
                Bound: 'Profile_Summary_Header.Badge.Bound',
                Issued: 'Profile_Summary_Header.Badge.Issued',
                Lost: 'Profile_Summary_Header.Badge.Lost',
                Cancelled: 'Profile_Summary_Header.Badge.Cancelled',
                'Quote Accepted': 'Profile_Summary_Header.Badge.QuoteAccepted',
                Quoting: 'Profile_Summary_Header.Badge.Quoting',
            },
        };

        const translationId = mappings[type][text];

        return translationId ? intl.formatMessage({ id: translationId }) : text;
    };

    const { policySummary } = props;
    const showIndicativeQuoteBadge = policySummary.isIndicativeQuote && policySummary.status === 'quoted';
    const isCardAuthorized = policySummary?.isCardAuthorized === true;
    const isAuthorizationCharged = policySummary.isAuthorizationCharged === true;
    const authorizationStatus = isCardAuthorized ? 'Authorized' : null;
    const paymentStatus = policySummary.badgePaymentStatus;
    const policyStatus = showIndicativeQuoteBadge ? 'Quoted (indicative)' : policySummary.badgePolicyStatusDescription;
    const documentIssueStatus = policySummary.badgeDocumentIssueStatus;

    const showAuthorizationBadge = !isAuthorizationCharged && authorizationStatus !== null;
    const showPaymentBadge = paymentStatus ?? false;
    const showPolicyBadge = policyStatus ?? false;
    const showDocumentBadge = documentIssueStatus ?? false;
    const showHeaderContentFlex = showAuthorizationBadge || showPaymentBadge || showPolicyBadge || showDocumentBadge;

    return (
        <Container fullWidth>
            <Grid>
                <Row>
                    <Column>
                        <Container inline top={sizes.xl} horizontalAlignment={alignments.center}>
                            {renderIcon(policySummary.status)}
                        </Container>
                        <Header
                            size={sizes.xl}
                            top={sizes.sm}
                            bottom={sizes.xs}
                            text={<FormattedMessage id={`Profile_Summary_Header.${policySummary.status}Header`} />}
                        />
                        <Label text={<FormattedMessage id={`Profile_Summary_Header.${policySummary.status}Label`} />} />
                        {showHeaderContentFlex && (
                            <HeaderContentFlex>
                                {showAuthorizationBadge && (
                                    <Badge
                                        text={getBadgeText(authorizationStatus, 'badgeAuthorizationStatus')}
                                        color={getBadgeColor(authorizationStatus, 'badgeAuthorizationStatus')}
                                    />
                                )}
                                {showPaymentBadge && (
                                    <Badge
                                        text={getBadgeText(paymentStatus, 'badgePaymentStatus')}
                                        color={getBadgeColor(paymentStatus, 'badgePaymentStatus')}
                                    />
                                )}
                                {showPolicyBadge && (
                                    <Badge
                                        text={getBadgeText(policyStatus, 'badgePolicyStatusDescription')}
                                        color={getBadgeColor(policyStatus, 'badgePolicyStatusDescription')}
                                    />
                                )}
                                {showDocumentBadge && (
                                    <Badge
                                        text={getBadgeText(documentIssueStatus, 'badgeDocumentIssueStatus')}
                                        color={getBadgeColor(documentIssueStatus, 'badgeDocumentIssueStatus')}
                                    />
                                )}
                            </HeaderContentFlex>
                        )}
                    </Column>
                </Row>
            </Grid>
            <Separator solid top={sizes.lg} />
        </Container>
    );
};

export default ProfileSummaryHeader;
